import axios from 'axios';
import * as ReactQuery from 'react-query';
import { ReliningImageNameEnum } from '@trace/types';
import { DocumentRequestBase } from '../../../services/documents';
import { identify, makeRequest, Service } from '../../../utils/http';
import { InferMutationOptions } from '../../../utils/types';

type UploadMachinePictureData = DocumentRequestBase & {
  file: File;
};

/*
 * Uploads a single machine picture for a relining entry.
 *
 * Re-uses the existing relining upload-url mechanism (the same one used for
 * the other relining media), which stores the object publicly-readable in the
 * documents S3 bucket and returns a stable public URL. The image is streamed
 * as-is (no compression) so the full-size picture can be opened from the
 * generated certificate.
 *
 * The resulting public URL is returned to the caller, which writes it into the
 * form data so it is persisted on autosave and later embedded in the
 * generated certificate.
 */
async function uploadReliningMachinePictureApi({
  file,
  templateId,
  documentId,
}: UploadMachinePictureData) {
  const { signedUrl, uploadUrl } = await makeRequest(
    Service.getReliningUploadImageUrl(
      documentId,
      templateId,
      ReliningImageNameEnum.MACHINE_PICTURE,
      await identify(),
    ),
  );

  await axios.put(signedUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });

  return {
    uploadUrl,
    fileName: file.name,
    timestamp: Date.now(),
  };
}

function useUploadReliningMachinePicture(
  options?: InferMutationOptions<typeof uploadReliningMachinePictureApi>,
) {
  const { mutate: uploadReliningMachinePicture, ...rest } = ReactQuery.useMutation(
    'uploadReliningMachinePicture',
    uploadReliningMachinePictureApi,
    options,
  );

  return {
    uploadReliningMachinePicture,
    ...rest,
  };
}

export { useUploadReliningMachinePicture };

